import {
  calculateLeadScore,
  getLeadTemperature
} from "./leadScoring";

export function getAiSalesRecommendation(lead) {
  const score = lead.score || calculateLeadScore(lead);
  const stage = lead.stage || "New";
  const hasFollowUp = Boolean(lead.nextFollowUp);
  const hasContacted = Boolean(lead.lastContactedAt);

  if (stage === "Won") {
    return {
      priority: "Closed Won",
      action: "Maintain relationship and explore expansion opportunities.",
      reason: "This lead is already marked as won."
    };
  }

  if (stage === "Lost") {
    return {
      priority: "Closed Lost",
      action: "Review notes and consider a future re-engagement campaign.",
      reason: "This lead is marked as lost."
    };
  }

  if (score >= 80 && !hasContacted) {
    return {
      priority: "High Priority",
      action: "Contact this lead within 24 hours and offer a discovery call.",
      reason: "This is a hot lead and has not yet been contacted."
    };
  }

  if (score >= 80 && !hasFollowUp) {
    return {
      priority: "High Priority",
      action: "Set a follow-up date and move the lead toward qualification.",
      reason: "This is a hot lead but no next follow-up is scheduled."
    };
  }

  if (score >= 50) {
    return {
      priority: "Medium Priority",
      action: "Nurture this lead with a short follow-up message and relevant ESG offer.",
      reason: "This lead shows moderate commercial potential."
    };
  }

  return {
    priority: "Low Priority",
    action: "Keep this lead in the CRM and follow up when capacity allows.",
    reason: "This lead has a lower score or limited buying signals."
  };
}

export function getConversionProbability(lead) {
  let probability = 5;

  const score = lead.score || calculateLeadScore(lead);

  // Lead score influence
  probability += score * 0.5;

  // Pipeline stage influence
  switch (lead.stage) {
    case "Contacted":
      probability += 10;
      break;
    case "Qualified":
      probability += 20;
      break;
    case "Proposal":
      probability += 30;
      break;
    case "Won":
      return 100;
    case "Lost":
      return 0;
    default:
      break;
  }

  // Contact activity
  if (lead.lastContactedAt) {
    probability += 10;
  }

  // Follow-up scheduled
  if (lead.nextFollowUp) {
    probability += 5;
  }

  // Notes history shows engagement
  if (lead.notesTimeline?.length > 0) {
    probability += Math.min(
      lead.notesTimeline.length * 2,
      10
    );
  }

  return Math.max(0, Math.min(100, Math.round(probability)));
}

export function generateFollowUpEmail(lead) {
  const firstName = String(lead.name || "there").split(" ")[0];
  const company = lead.company || "your organization";
  const interest = lead.interest || "Ecovanta";
  const stage = lead.stage || "New";
  const probability = getConversionProbability(lead);

  let subject = `Following up on your ${interest} request`;

  let body = `Hello ${firstName},

Thank you for your interest in ${interest}.

I wanted to follow up and see whether you would be open to a short discovery call to discuss how Ecovanta can support ${company}'s ESG priorities.

Based on your request, we can help you better understand your ESG maturity, identify priority gaps, and define practical next steps.

Would you be available for a brief call this week or next?

Best regards,
The Ecovanta Team`;

  if (stage === "Qualified" || probability >= 70) {
    subject = `Next steps for ${company}'s ESG priorities`;

    body = `Hello ${firstName},

Thank you again for your interest in ${interest}.

Based on the information shared, ${company} appears to be a strong fit for Ecovanta's ESG advisory and platform support.

I would suggest a short discovery call to review your ESG priorities, current maturity level, and potential next steps.

Would you be available this week to discuss?

Best regards,
The Ecovanta Team`;
  }

  if (stage === "Proposal") {
    subject = `Following up on the Ecovanta proposal`;

    body = `Hello ${firstName},

I wanted to follow up on the Ecovanta proposal and check whether you had any questions.

We would be happy to walk through the proposed ESG roadmap, deliverables, and next steps with you.

Would you like to schedule a short call to review?

Best regards,
The Ecovanta Team`;
  }

  return { subject, body };
}

export function generateMeetingBrief(lead) {
  const company = lead.company || "the organization";
  const industry = lead.industry || "not specified";
  const stage = lead.stage || "New";
  const probability = getConversionProbability(lead);
  const score = lead.score || calculateLeadScore(lead);
  const notesCount = Array.isArray(lead.notesTimeline)
    ? lead.notesTimeline.length
    : 0;

  let objective = "Understand the lead's ESG priorities and qualify whether Ecovanta can support their needs.";

  if (stage === "Qualified" || probability >= 70) {
    objective = "Confirm priority ESG needs and move the lead toward a proposal or implementation discussion.";
  }

  if (stage === "Proposal") {
    objective = "Address proposal questions, confirm decision criteria, and agree on next steps.";
  }

  const talkingPoints = [
    "Clarify current ESG maturity and reporting challenges.",
    "Discuss ESG Gap Analysis Pro and how it can identify priority improvement areas.",
    "Explore whether the organization needs advisory support, platform access, or both.",
    "Confirm timeline, decision-maker involvement, and budget readiness."
  ];

  const potentialPainPoints = [
    "Limited internal ESG capacity.",
    "Difficulty collecting ESG data across teams.",
    "Unclear ESG priorities or roadmap.",
    "Pressure to improve reporting, compliance, or stakeholder communication."
  ];

  const suggestedQuestions = [
    `What are ${company}'s most urgent ESG priorities right now?`,
    "Who owns ESG reporting and sustainability decisions internally?",
    "What ESG data is currently being tracked?",
    "Is there a target date for improving ESG reporting or maturity?"
  ];

  const suggestedNextStep =
    probability >= 70
      ? "Recommend a discovery call or proposal review focused on ESG roadmap implementation."
      : "Recommend a short qualification call and offer the ESG Maturity Benchmark as a starting point.";

  return {
    objective,
    talkingPoints,
    potentialPainPoints,
    suggestedQuestions,
    suggestedNextStep,
    context: {
      company,
      industry,
      stage,
      score,
      probability,
      notesCount
    }
  };
}